import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { post } from '../api'
import { saveChatSession } from '../chat/session-storage'

interface ResolveResponse {
  sessionId: string
  wsToken: string
  wsTokenExpiresAt: number
  sessionExpiresAt: number
  subscriberName: string
  agentDisplayName: string | null
  policy: { id: string }
}

export function CustomerJoin() {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  function onChange(value: string) {
    // Auto-format: uppercase, strip non-alphanumeric, insert dash after 4.
    const cleaned = value.toUpperCase().replace(/[^0-9A-HJKMNP-TV-Z]/g, '').slice(0, 8)
    setCode(cleaned.length > 4 ? `${cleaned.slice(0, 4)}-${cleaned.slice(4)}` : cleaned)
  }

  async function onPaste() {
    try {
      const text = await navigator.clipboard.readText()
      onChange(text)
    } catch {
      // Permissions may deny — silently no-op; user can type instead.
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      const r = await post<ResolveResponse>('/api/sessions/resolve', { code })
      // Persist via the centralised helper (DRY) so the chatroom can
      // render the live SessionCountdown without re-querying the API.
      saveChatSession(r.sessionId, {
        wsToken: r.wsToken,
        subscriberName: r.subscriberName,
        agentDisplayName: r.agentDisplayName,
        expiresAt: r.sessionExpiresAt,
      })
      navigate(`/room/${r.sessionId}`)
    } catch {
      // v0.2 /resolve no longer requires a customer cookie (auto-creates
      // on first join), so the only error path here is a bad / expired
      // code. We keep the message uniform to avoid enumeration.
      setError('Code invalid or expired')
    }
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
      <h1 className="text-xl font-semibold mb-1">Enter Secure Code</h1>
      <p className="text-sm text-slate-600 mb-6">
        Your agent will provide a one-time code to open a private chat room.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1">Session Code</span>
          <input
            value={code}
            onChange={(e) => onChange(e.target.value)}
            placeholder="XXXX-XXXX"
            className="w-full text-center font-mono text-lg tracking-[0.3em] uppercase rounded-lg border border-slate-300 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            required
          />
        </label>
        <div className="flex gap-3">
          <button
            type="submit"
            className="flex-1 rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700"
          >
            Join
          </button>
          <button
            type="button"
            onClick={onPaste}
            className="flex-1 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium px-4 py-2 hover:bg-slate-50"
          >
            Paste
          </button>
        </div>
      </form>

      {error && (
        <div className="mt-6 p-4 bg-rose-50 border border-rose-100 rounded-lg text-sm text-rose-900">
          {error}
        </div>
      )}

      <p className="mt-8 text-xs text-slate-500">
        By joining, you agree not to share sensitive info unless requested by
        your company. Sessions may be logged for compliance.
      </p>
    </div>
  )
}
